// src/Frameworks/components/AssistantPanel.jsx
import React, { useMemo, useState } from "react";
import { useBilling } from "../billing/BillingClient";

/**
 * Props:
 *  - payload, onSubmitText(text), onSubmitChip(chip), busy
 *  - hasActiveSession?: boolean
 *  - hasCredits?: boolean
 *  - onStartSession?: () => Promise<any>
 *  - onBuyAndStart?: () => Promise<any>
 *  - disableInput?: boolean
 *
 * NOTE: No local timers here. Countdown comes from BillingProvider.
 */
export default function AssistantPanel({
  payload,
  onSubmitText,
  onSubmitChip,
  busy = false,
  hasActiveSession = true,
  hasCredits = false,
  onStartSession,
  onBuyAndStart,
  disableInput = false,
}) {
  const [text, setText] = useState("");
  const { secondsRemaining } = useBilling(); // single source of truth

  // Normalize chip payloads
  const chips = useMemo(() => {
    const raw = Array.isArray(payload?.chips) ? payload.chips : [];
    return raw
      .map((c) => (typeof c === "string" ? c : c?.label ?? c?.text ?? c?.value))
      .filter(Boolean)
      .slice(0, 3);
  }, [payload?.chips]);

  const isChips =
    (payload?.ui === "chips" || payload?.ui === "chip") && chips.length > 0;

  const statement =
    payload?.body ??
    payload?.assistant_prompt_text ??
    payload?.assistantPromptText ??
    payload?.message ??
    payload?.text ??
    payload?.statement ??
    "What’s on your mind?";

  // One button, three modes:
  const ctaMode = hasActiveSession
    ? "submit"
    : hasCredits
    ? "start_session"
    : "buy_start";

  const ctaLabel =
    ctaMode === "submit"
      ? "Submit"
      : ctaMode === "start_session"
      ? "Start session"
      : "Start a session for $1.99";

  const typingDisabled = !!disableInput;
  const buttonDisabled = !!busy || (ctaMode === "submit" && text.trim() === "");

  async function handleCTAFromText(value) {
    if (busy) return;

    if (ctaMode === "submit") {
      const v = (value || "").trim();
      if (!v) return;
      await onSubmitText?.(v);
      setText("");
      return;
    }

    if (ctaMode === "start_session") {
      await onStartSession?.();
      return;
    }

    // buy + start
    await onBuyAndStart?.();
  }

  async function handleCTAFromChip(chip) {
    if (busy) return;

    if (ctaMode === "submit") {
      await onSubmitChip?.(chip);
      return;
    }
    if (ctaMode === "start_session") {
      await onStartSession?.();
      return;
    }
    await onBuyAndStart?.();
  }

  // Format timer (shown once, right side of the submit row)
  const showTimer = hasActiveSession && Number.isInteger(secondsRemaining);
  const timerText = showTimer
    ? `${Math.floor(secondsRemaining / 60)}:${String(
        secondsRemaining % 60
      ).padStart(2, "0")}`
    : null;

  return (
    <div className="assistant-panel glass-overlay" aria-busy={busy}>
      <div className="assistant-title">Vybly</div>
      <div className="assistant-body">
        <div className="assistant-statement">{statement}</div>

        {isChips ? (
          <div className="chips-row">
            {chips.map((c, idx) => (
              <button
                key={idx}
                className="chip"
                onClick={() => handleCTAFromChip(c)}
                disabled={busy}
              >
                {c}
              </button>
            ))}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (ctaMode === "submit") {
                if (buttonDisabled) return;
                handleCTAFromText(text);
              } else {
                if (busy) return;
                handleCTAFromText(text); // allow empty text for buy/start
              }
            }}
            className="assistant-form"
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type up to ~500 words…"
              maxLength={4000}
              disabled={typingDisabled}
            />

            {/* Submit row: button left, timer right (only one timer) */}
            <div className="assistant-cta-row">
              <button
                type="submit"
                className="btn-primary"
                disabled={buttonDisabled}
                aria-label={ctaLabel}
                title={ctaLabel}
                onClick={(e) => {
                  if (buttonDisabled) return;
                  e.preventDefault();
                  handleCTAFromText(text);
                }}
              >
                {ctaLabel}
              </button>

              {showTimer && <span className="session-timer">{timerText}</span>}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
